import { IncomeDTO, IncomeModel, IncomeType, OwnerModelDTO } from '../../api/useIncomeQuery';
import { getDefaultCustomBucketIndex } from '../../utils/getDefaultCustomBucketIndex';

export type OtherIncomeBody = Omit<OtherIncomeFormValues, 'accountEarmarks'> & {
  AccountEarmark: { Type: string }[];
};

export interface UpsertIncomeDTO extends OtherIncomeBody {
  clientId: string;
  id: string | null;
  incomeType: IncomeType;
}

export interface OtherIncomeFormValues {
  name: string;
  earner: string;
  amount: number | null;
  startAge: number | null | undefined;
  endAge: number | null | undefined;
  endAtRetirement: boolean | null;
  endAtEndOfPlan: boolean | null;
  accountEarmarks: string[];
  inflation: number | null;
  isTaxFree: boolean | null;
  orionManagementStyleId: number | null;
  customBucketIndex: number | null;
  ownerModel: OwnerModelDTO[];
}

const getEndAge = (income: IncomeModel) => {
  if (income.EndAtRetirement) {
    return null;
  }
  if (income.EndAtEndOfPlan) {
    return -1;
  }
  if (income.EndAge) {
    return income.EndAge;
  }
  return undefined;
};

const defaultValues = {
  name: '',
  amount: null,
  startAge: undefined,
  endAge: undefined,
  endAtRetirement: false,
  endAtEndOfPlan: false,
  accountEarmarks: [],
  inflation: null,
  isTaxFree: false,
  orionManagementStyleId: null,
  customBucketIndex: null,
};

export const dataToForm = (income: IncomeModel | null, data: IncomeDTO): OtherIncomeFormValues => {
  const { Options: options } = data;

  if (!income) {
    return {
      ...defaultValues,
      earner: options.OwnerOptions[0],
      ownerModel: [
        {
          Id: null,
          Name: options.OwnerOptions.People[0].Name,
          OwnerType: 0,
          Percentage: 1,
        },
      ],
    };
  }

  return {
    name: income.Name,
    earner: income.Earner,
    amount: income.Amount,
    startAge: income.StartAge,
    endAge: getEndAge(income),
    endAtRetirement: income.EndAtRetirement,
    endAtEndOfPlan: income.EndAtEndOfPlan,
    accountEarmarks: income.AccountEarmark.map(earmark => earmark.Type),
    inflation: income.Inflation,
    isTaxFree: income.IsTaxFree,
    orionManagementStyleId: income.OrionManagementStyleId,
    customBucketIndex: getDefaultCustomBucketIndex({
      isAssetBucketingByManagementStyle: options.AssetBucketingByManagementStyle,
      customBucketIndex: income.CustomBucketIndex,
      orionManagementStyleId: income.OrionManagementStyleId,
      managementStylesOptions: options.ManagementStyles,
    }),
    ownerModel: income.OwnerModel,
  };
};

export const formToBody = (values: OtherIncomeFormValues): OtherIncomeBody => ({
  name: values.name,
  earner: values.earner,
  amount: values.amount,
  startAge: values.startAge,
  endAge: values.endAtRetirement || values.endAtEndOfPlan ? null : values.endAge,
  endAtRetirement: values.endAtRetirement,
  endAtEndOfPlan: values.endAtEndOfPlan,
  inflation: values.inflation,
  isTaxFree: values.isTaxFree,
  orionManagementStyleId: values.orionManagementStyleId,
  customBucketIndex: values.customBucketIndex,
  ownerModel: values.ownerModel,
  AccountEarmark: values.accountEarmarks.map(earmark => ({ Type: earmark })),
});
